import { collabStateManager } from '@databases/collab-state-manager';
import { Properties } from '@databases/collab/y-database';
import { subscribeViewProjectionChanges, viewState } from '@databases/database-state';
import { container } from '@databases/service-container';
import { shouldShowField } from '@databases/types/database/view';
import { type TableV2Instance } from '@databases/view-layout-table-v2/data';
import { type Transaction } from 'yjs';
import { createTableV2DataSourceFromSdk } from './create-table-v2-data-source-from-sdk.ts';

/**
 * Adapts the active Yjs document to V2 rendering snapshots. This detects source
 * changes; it does not reconcile selections or place highlights. The view session
 * publishes semantic ordering immediately for selection reconciliation. Presence
 * consumes a separate committed projection after matching geometry is available.
 *
 * afterTransaction runs after the model wrappers update. Inspecting changed Yjs
 * collections avoids enumerating IDs or fields for ordinary cell-value edits.
 * Attachment and disposal belong to bootstrap, so old readers cannot reach a
 * replacement document. See README.md for the source-to-projection boundary.
 */
export function createTableV2LiveSource(contentId: string) {
  let snapshot: Omit<TableV2Instance, 'dispose'> | undefined;
  let stopDocument = () => {};
  const listeners = new Set<VoidFunction>();
  const notify = () => listeners.forEach((listener) => listener());

  const stopAttachment = collabStateManager.onCollabManagerAttached(
    contentId,
    ({ database }) => {
      stopDocument();

      const reads = createTableV2DataSourceFromSdk(container.resolve('DatabaseSdk'));
      let active = true;

      let fields = Object.freeze([...reads.listFields()]);
      let entryIds = Object.freeze([...reads.listEntryIds()]);
      let visibleFieldIds: readonly string[] = [];

      const document = database.doc;
      const entries = document.getArray(Properties.entries);
      const entriesById = document.getMap(Properties.entriesById);
      const fieldOrder = document.getArray(Properties.fields);
      const fieldsById = document.getMap(Properties.fieldsById);

      const publishSnapshot = () => {
        const nextVisibleFieldIds = fields
          .filter(({ id }) => shouldShowField(viewState.view.settings.fields, id))
          .map(({ id }) => id);

        if (
          nextVisibleFieldIds.length !== visibleFieldIds.length ||
          !nextVisibleFieldIds.every((id, index) => id === visibleFieldIds[index])
        ) {
          visibleFieldIds = Object.freeze(nextVisibleFieldIds);
        }

        const previous = snapshot?.projection;

        if (
          previous?.fields === fields &&
          previous.entryIds === entryIds &&
          previous.visibleFieldIds === visibleFieldIds
        ) {
          return;
        }

        snapshot = {
          projection: Object.freeze({ fields, entryIds, visibleFieldIds }),
          getEntry: (id) => (active ? reads.getEntry(id) : undefined),
          getValue: (address) => (active ? reads.getValue(address) : undefined),
          getDefaultValue: (id) => (active ? reads.getDefaultValue(id) : undefined),
        };
        notify();
      };

      const onAfterTransaction = (transaction: Transaction) => {
        const changed: ReadonlyMap<object, unknown> = transaction.changed;
        const changedParents: ReadonlyMap<object, unknown> =
          transaction.changedParentTypes;
        const entriesChanged = changed.has(entries) || changed.has(entriesById);
        const fieldsChanged = changed.has(fieldOrder) || changedParents.has(fieldsById);

        if (!entriesChanged && !fieldsChanged) {
          return;
        }

        if (entriesChanged) {
          entryIds = Object.freeze([...reads.listEntryIds()]);
        }

        if (fieldsChanged) {
          fields = Object.freeze([...reads.listFields()]);
        }

        publishSnapshot();
      };

      const onDestroy = () => {
        stopDocument();
        snapshot = undefined;
        notify();
      };

      document.on('afterTransaction', onAfterTransaction);
      document.on('destroy', onDestroy);
      const stopView = subscribeViewProjectionChanges(publishSnapshot);

      stopDocument = () => {
        active = false;
        document.off('afterTransaction', onAfterTransaction);
        document.off('destroy', onDestroy);
        stopView();
      };

      publishSnapshot();
    },
  );

  return {
    getSnapshot: () => snapshot,
    subscribe(listener: VoidFunction) {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
    dispose() {
      stopAttachment();
      stopDocument();
      snapshot = undefined;
      listeners.clear();
    },
  };
}
